import { API_BASE_URL } from '../config';
import { ensureCsrfCookie, withCsrfHeaders } from '../utils/csrf';

export type AdminLoginResponse = {
  email: string;
  name: string;
  mustChangePassword: boolean;
};

const CREDENTIALS: RequestInit = { credentials: 'include' };

async function mutatingFetch(url: string, init: RequestInit): Promise<Response> {
  await ensureCsrfCookie();
  const headers = withCsrfHeaders({
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  return fetch(url, {
    ...CREDENTIALS,
    ...init,
    headers,
  });
}

async function readMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
  } catch {
    // ignore non-JSON
  }
  return fallback;
}

function parseLogin(data: unknown, fallbackEmail: string): AdminLoginResponse {
  const row = data as Partial<AdminLoginResponse> | null;
  return {
    email: typeof row?.email === 'string' ? row.email : fallbackEmail,
    name: typeof row?.name === 'string' ? row.name : '',
    mustChangePassword: row?.mustChangePassword === true,
  };
}

export async function loginAdmin(email: string, password: string): Promise<AdminLoginResponse> {
  const res = await mutatingFetch(`${API_BASE_URL}/api/auth/admin/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (res.status === 401) {
    throw new Error(await readMessage(res, 'メールアドレスまたはパスワードが正しくありません。'));
  }
  if (res.status === 429) {
    throw new Error(await readMessage(res, '試行回数が上限に達しました。しばらくしてから再度お試しください。'));
  }
  if (!res.ok) {
    throw new Error(await readMessage(res, 'ログインに失敗しました。'));
  }
  const parsed = parseLogin(await res.json(), email);
  try {
    await fetchAdminMe();
    await ensureCsrfCookie();
  } catch {
    throw new Error(
      'ログイン応答は成功しましたが、セッションCookieが保存されていません。' +
        'http://localhost:3000/admin で開いているか、フロント開発サーバーを再起動してください。'
    );
  }
  return parsed;
}

export async function fetchAdminMe(): Promise<AdminLoginResponse> {
  const res = await fetch(`${API_BASE_URL}/api/auth/admin/me`, CREDENTIALS);
  if (!res.ok) {
    throw new Error(await readMessage(res, 'セッションが無効です。'));
  }
  return parseLogin(await res.json(), '');
}

export async function logoutAdmin(): Promise<void> {
  try {
    await mutatingFetch(`${API_BASE_URL}/api/auth/admin/logout`, { method: 'POST' });
  } catch {
    // still clear local UI session
  }
}

export async function requestAdminPasswordReset(email: string): Promise<string> {
  const res = await mutatingFetch(`${API_BASE_URL}/api/auth/admin/forgot-password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  const fallbackOk = 'ご入力のメールアドレスが登録されている場合、仮パスワードを送信しました。';
  if (res.status === 429) {
    throw new Error(await readMessage(res, '試行回数が上限に達しました。しばらくしてから再度お試しください。'));
  }
  if (res.status === 503) {
    throw new Error(await readMessage(res, 'メールの送信に失敗しました。しばらくしてから再度お試しください。'));
  }
  if (!res.ok) {
    throw new Error(await readMessage(res, '送信に失敗しました。'));
  }
  return readMessage(res, fallbackOk);
}

export async function changeAdminPassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const res = await mutatingFetch(`${API_BASE_URL}/api/auth/admin/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    throw new Error(await readMessage(res, 'パスワードの変更に失敗しました。'));
  }
}
